import type { Claim, ExpenseSummary } from '../../../../types/LegalCase';
import { formatCurrency } from '../../../../utils/collateralFormatters';

// Resultado esperado do caso: confronta o que ainda dá para recuperar (pretensão) com o que o
// caso já custou (despesas). NÃO soma custo na pretensão — são naturezas distintas (o que se
// cobra do devedor vs. o que a Agrolend gastou); apenas confronta. Versão enxuta (Task 11): o
// recuperável não desconta depósitos levantados — esses aparecem à parte na seção Depósitos.
export interface CaseResult {
    // Nenhum dos dois lados existe (sem pretensão e sem custo): não há o que confrontar.
    hasData: boolean;
    hasClaim: boolean;
    hasCost: boolean;
    recoverable: number;
    recoverableFormatted: string;
    cost: number;
    costFormatted: string;
    // net = recoverable - cost. Só faz sentido quando há pretensão (senão é só "-custo").
    net: number;
    netFormatted: string;
    // Resultado só é significativo quando há pretensão para confrontar.
    hasNet: boolean;
    // Custo como % do recuperável (custo/recuperável*100). Responde "vale a pena?": 15% ok,
    // 90% acende alerta. Só faz sentido com recuperável > 0. NÃO é capado em 100 — custo acima
    // do recuperável (>100%) é justamente o sinal que interessa.
    costRatioPct: number;
    costRatioFormatted: string;
    hasCostRatio: boolean;
}

// "Recuperável" = saldo remanescente da pretensão quando há pagamento registrado; senão o
// total cheio da pretensão (na fase manual muitos casos não têm pagamento lançado, e aí o
// recuperável é a pretensão inteira). Deriva do backend (grandTotal/remainingTotal/paidTotal).
const recoverableFromClaim = (claim: Claim | null): number => {
    if (!claim) return 0;
    if (claim.paidTotal > 0) return claim.remainingTotal || 0;
    return claim.grandTotal || 0;
};

export const computeCaseResult = (
    claim: Claim | null,
    expensesSummary: ExpenseSummary | null | undefined,
): CaseResult => {
    const hasClaim = !!claim && (claim.grandTotal > 0 || claim.remainingTotal > 0);
    const cost = expensesSummary?.total || 0;
    const hasCost = cost > 0;
    const recoverable = recoverableFromClaim(claim);
    const net = recoverable - cost;

    // % do custo sobre o recuperável — só depende de haver saldo (denominador). Custo zero dá
    // 0% (métrica válida, não escondida): um caso com saldo e sem despesa mostra "0%". Sem
    // saldo a recuperar não há denominador, aí a % não faz sentido e é omitida.
    const hasCostRatio = recoverable > 0;
    const costRatioPct = hasCostRatio ? (cost / recoverable) * 100 : 0;

    return {
        hasData: hasClaim || hasCost,
        hasClaim,
        hasCost,
        recoverable,
        recoverableFormatted: formatCurrency(recoverable),
        cost,
        costFormatted: formatCurrency(cost),
        net,
        netFormatted: formatCurrency(net),
        hasNet: hasClaim,
        costRatioPct,
        costRatioFormatted: `${costRatioPct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
        hasCostRatio,
    };
};
